import { FC, useContext } from 'react';
import {
  EMPTY_RESULT,
  formatScore,
  formatScoreWithUnit,
  getEffectiveEvaluationResult,
  getExpertReportResultText,
  hasPointsScale,
} from '@/entities/expert-report';
import { ExpertReportResultEditContext } from '../model';
import { EvaluationBlockResultEditor } from './EvaluationBlockResultEditor';
import type { FlatRow } from './ExpertReportTab';

type Props = {
  row: FlatRow;
  conversationId: number;
  expertReportId: number;
};

/**
 * Результат строки заключения: балл вида `рейтинг / максимум` с размерностью, если она определена,
 * значение справочника, набор таких значений — или прочерк, когда результата нет. Строка читается
 * действующим результатом: выставленный пользователем старше полученного системой, в какой бы форме
 * каждый из них ни был.
 *
 * Результат-значение максимума и размерности не имеет и в числовые итоги не входит — в том числе
 * у блока, шкала которого несёт и баллы, и справочник. Правятся результаты только тех блоков,
 * которые оценивает система сама: у итога и у блока с вложенными блоками результат складывается
 * по правилу расчёта и остаётся текстом.
 *
 * Без разрешения на правку результата любой результат остаётся текстом: читателю карточки диалога
 * правка закрыта, и предлагать её ему незачем.
 */
export const EvaluationBlockResult: FC<Props> = ({ row, conversationId, expertReportId }) => {
  const canEdit = useContext(ExpertReportResultEditContext);
  const isEditable = canEdit && !row.isTotal && !row.isParent;
  const result = getEffectiveEvaluationResult(row);

  // Набор складывается правилом расчёта — выбирать в нём значение не предлагается. Ужиматься ему,
  // в отличие от балла и одного значения, не запрещено: перечисление переносится по строкам,
  // а не выдавливает наименование блока
  if (result.values?.length) {
    return (
      <span className="text-muted-foreground min-w-0 text-right break-words">{getExpertReportResultText(result)}</span>
    );
  }

  // Справочник блока приходит с оценкой, даже когда значение получить не удалось, а баллы шкалы —
  // даже когда блок ответил значением: по ним и видно, в каких формах результат блока правится
  if (isEditable && (hasPointsScale(row) || row.possibleValues?.length)) {
    return <EvaluationBlockResultEditor row={row} conversationId={conversationId} expertReportId={expertReportId} />;
  }

  if (result.score == null) {
    // Результата нет вовсе: ни балла, ни значения — прочерк, а не ноль, которого никто не заработал
    return <span className="text-muted-foreground shrink-0">{result.value ?? EMPTY_RESULT}</span>;
  }

  return (
    <span className="text-muted-foreground shrink-0">
      {result.maxScore == null
        ? formatScore(result.score, result.unit)
        : formatScoreWithUnit(result.score, result.maxScore, result.unit)}
    </span>
  );
};
